import { Badge } from 'react-bootstrap';
import {
    PROGRAM_NAMES,
    PROGRAM_WINDOW_ORDER,
    describeProgramWindow,
} from '@/shared/utils/deadline';
import { inProgramDisplayOrder } from '@/shared/utils/programOrder';
import { isSwitchedOff } from '@/shared/utils/programInfo';
import { ProgramComingSoonText } from '@/components/common/ProgramComingSoon';

const sameVerdict = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/**
 * One labelled row per program in display order, or a single "All programs"
 * row when every program has the same verdict, so a term with one shared
 * window and no extensions reads as the single deadline it is. Switched-off
 * programs are never merged: each row links to its own program's page.
 */
export function programWindowRows(windows) {
    const rows = inProgramDisplayOrder(
        PROGRAM_WINDOW_ORDER.filter((key) => windows?.[key]).map((key) => ({
            key,
            label: `${PROGRAM_NAMES[key]} (${key.toUpperCase()})`,
            verdict: windows[key],
        }))
    );
    if (
        rows.length > 1 &&
        !isSwitchedOff(rows[0].verdict) &&
        rows.every((row) => sameVerdict(row.verdict, rows[0].verdict))
    ) {
        return [
            { key: 'all', label: 'All programs', verdict: rows[0].verdict },
        ];
    }
    return rows;
}

/**
 * The viewer's application window for each program, from the server's
 * per-caller verdicts on the current term (`term.applicationWindows`).
 */
function ProgramWindowList({ windows, className = '' }) {
    const now = new Date();
    return (
        <ul className={`list-unstyled mb-0 ${className}`}>
            {programWindowRows(windows).map(({ key, label, verdict }) => {
                if (isSwitchedOff(verdict)) {
                    return (
                        <li
                            key={key}
                            className="d-flex flex-wrap align-items-center gap-2 py-1"
                        >
                            <span className="fw-semibold">{label}</span>
                            <Badge bg="secondary">Coming soon</Badge>
                            <ProgramComingSoonText
                                programType={key}
                                className="text-muted small"
                            />
                        </li>
                    );
                }
                const { badge, variant, text } = describeProgramWindow(
                    verdict,
                    now
                );
                return (
                    <li
                        key={key}
                        className="d-flex flex-wrap align-items-center gap-2 py-1"
                    >
                        <span className="fw-semibold">{label}</span>
                        <Badge
                            bg={variant}
                            text={variant === 'warning' ? 'dark' : undefined}
                        >
                            {badge}
                        </Badge>
                        <span className="text-muted small">{text}</span>
                    </li>
                );
            })}
        </ul>
    );
}

export default ProgramWindowList;
